import { signal } from '@preact/signals';
import type { CastBridge, CastMediaSpec, CastStatus } from './native';
import { onNativeEvent, whenNative } from './native';

/**
 * Chromecast, as offered by the Android app.
 *
 * The Cast SDK is native — there is no Cast Web Sender inside a WebView, and the page has no way
 * to discover a device on the local network — so the shell owns the session and this module is a
 * client for it, exactly as `downloads.ts` is for the downloader. Everywhere the bridge is absent
 * (every browser, the iOS PWA, an Android build older than bridge protocol 2) `castAvailable`
 * stays false and no cast control renders.
 *
 * This module deliberately knows nothing about the `<audio>` element: who is playing what is
 * `player.ts`'s problem, and it subscribes through [onCastStatus].
 *
 * Deliberately free of DOM types — see the note in `test/downloads-state.test.ts`.
 */

/**
 * The fastest a Cast receiver will play.
 *
 * Anything above this is clamped on arrival — `CastController.load` and `CastController.setRate`
 * both coerce into range — so it is clamped on the way out too, and `player.ts` shows the clamped
 * value rather than a speed the TV is not playing at.
 */
export const CAST_MAX_RATE = 2;

/** The slowest. Below it a receiver reports an error rather than playing. */
const CAST_MIN_RATE = 0.5;

/** A receiver is on the network, or one is already connected. */
export const castAvailable = signal(false);
export const castConnected = signal(false);
export const castConnecting = signal(false);
export const castDevice = signal<string | null>(null);

let bridge: CastBridge | null = null;
let started = false;

const subscribers: ((status: CastStatus) => void)[] = [];

/**
 * Called once at boot. A no-op without the native shell, and without the cast namespace on it.
 *
 * The initial read matters as much as the subscription: a session survives the app being killed,
 * so the phone can come back to find something already playing on the TV, and no push will arrive
 * until that state next changes.
 */
export function initCast(): void {
  if (started) return;
  whenNative((native) => {
    if (started || !native.cast) return;
    started = true;
    bridge = native.cast;
    onNativeEvent((event) => {
      if (event?.type === 'cast' && event.state) apply(event.state);
    });
    void bridge
      .state()
      .then(apply)
      .catch(() => {
        // A failed first read leaves everything false, which is the safe way to be wrong: no
        // cast control renders and playback stays on the phone.
      });
  });
}

/** Subscribes to receiver state. Every frame, including the ones that change nothing visible. */
export function onCastStatus(callback: (status: CastStatus) => void): void {
  subscribers.push(callback);
}

/**
 * Test seam, and the counterpart to `resetDownloads`: drops subscribers and bridge state.
 *
 * `resetNative` is the caller's job — both features share one bridge, and resetting it from here
 * would pull it out from under the downloader.
 */
export function resetCast(): void {
  bridge = null;
  started = false;
  subscribers.length = 0;
  castAvailable.value = false;
  castConnected.value = false;
  castConnecting.value = false;
  castDevice.value = null;
}

// ------------------------------------------------------------------------ commands

/**
 * Opens the system device picker, or the controls for the device already in use.
 *
 * Discovery and the picker are the platform's, not the page's — a listener knows the standard
 * Cast dialog, and reimplementing it in the WebView would mean reimplementing discovery too.
 */
export async function showCastPicker(): Promise<void> {
  await bridge?.showPicker().catch(() => {});
}

/** Hands an episode to the receiver. Replaces whatever is playing there. */
export async function castLoad(spec: CastMediaSpec): Promise<void> {
  await bridge?.load({ ...spec, rate: clampRate(spec.rate) }).catch(() => {});
}

export async function castPlay(): Promise<void> {
  await bridge?.play().catch(() => {});
}

export async function castPause(): Promise<void> {
  await bridge?.pause().catch(() => {});
}

export async function castSeek(positionSec: number): Promise<void> {
  await bridge?.seek(Math.max(0, positionSec)).catch(() => {});
}

export async function castSetRate(rate: number): Promise<void> {
  await bridge?.setRate(clampRate(rate)).catch(() => {});
}

/** Ends the session. The receiver returns to its own screen and playback comes back to the phone. */
export async function stopCasting(): Promise<void> {
  await bridge?.stop().catch(() => {});
}

// ----------------------------------------------------------------------- internals

/** What a receiver will actually honour. A NaN rate would reach the bridge as `null` otherwise. */
function clampRate(rate: number): number {
  if (!Number.isFinite(rate)) return 1;
  return Math.min(Math.max(rate, CAST_MIN_RATE), CAST_MAX_RATE);
}

function apply(status: CastStatus): void {
  castAvailable.value = status.available;
  castConnected.value = status.connected;
  castConnecting.value = status.connecting;
  castDevice.value = status.deviceName ?? null;

  for (const subscriber of subscribers) {
    try {
      subscriber(status);
    } catch {
      // A throwing subscriber must not cost the rest of them the frame.
    }
  }
}
